/**
 * Recovery for a stale tab after a deploy.
 *
 * Every page is a lazy chunk with a content hash in its filename. A tab opened
 * before a deploy still asks for the old hashes, the server no longer has them,
 * and the dynamic import rejects — which the error boundary then shows as
 * "Something broke on this screen". A reload fetches the new index.html and the
 * new hashes, so that is the whole fix.
 *
 * The reload is rate-limited through sessionStorage: if the chunk is still
 * missing after a fresh load (a broken deploy, the network is down), reloading
 * again would loop forever, so the second failure falls through to the boundary.
 */

const KEY = 'consolidateai:chunk-reload-at';
const MIN_INTERVAL_MS = 10_000;

const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading (CSS )?chunk .* failed|Unable to preload CSS/i;

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return CHUNK_ERROR.test(message);
}

/** Reloads the page unless it already did so moments ago. Returns whether it reloaded. */
export function reloadOnceForChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? 0);
    if (Date.now() - last < MIN_INTERVAL_MS) return false;
    sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    // Storage blocked: without the guard a reload could loop, so let the
    // boundary show the error instead.
    return false;
  }
  window.location.reload();
  return true;
}
